import { Box, useTheme } from "@mui/material";
import { useTranslation } from 'react-i18next';
import ShLabel from "../../../../components/theme/label/ShLabel";
import Text from "../../../../components/theme/text/Text";
import { tokens } from "../../../../theme";

import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import OrdersCard from "../../../../components/global/cards/orders/OrdersCard";
import ShDataGrid3 from "../../../../components/theme/datagrid3/ShDataGrid3";
import { RELATION_ORDERS_DEFAULT } from "../../../../components/theme/datagrid3/groupingPresets/GroupingPresets";
import { RELATION_ORDERS_FAMILY } from "../family";
import ShCopyButton from '../../../../components/theme/ShCopy';
import SidepanelLoader from "../../../../components/theme/sidepanel2/SidepanelLoader";
import ShLabelSkeleton from "../../../../components/theme/skeleton/ShLabelSkeleton";
import ShTextSkeleton from "../../../../components/theme/skeleton/ShTextSkeleton";
import { useGetOrdersQuery, useBulkUpdateOrdersMutation, useExportOrdersMutation, useBulkCancelOrdersMutation, useBulkSplitBackordersMutation } from "../../../../newapi/order/ordersSlice";
import { getOrderStateData } from "../../../../utils/labelColorText";
import { general_states } from "../../../../utils/staticEnums";
import { ORDER_FILTER_KEYS } from "../../../../components/global/DataGrid/Filter";
import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';
import SplitscreenIcon from '@mui/icons-material/Splitscreen';
import ButtonGroup from "../../../../components/theme/buttons/ButtonGroup";
import Shbutton from "../../../../components/theme/buttons/Shbutton";
import { selectUserPermission } from "../../../../newapi/user/authentication/authSlice";
import { UserPrivileges } from '../../../../utils/permissions';
import MpAddOrder from "../../../../components/global/ModalsProgression/MpAddOrder/MpAddOrder";
import MpAddOrderRelation from "../../../../components/global/ModalsProgression/MpAddOrderRelation/MpAddOrderRelation";
import ShDataGridSelectedActions from "../../../../components/theme/datagrid2/ShDataGridSelectedActions";
import DropdownButton from "../../../../components/theme/buttons/DropdownButton";
import { showToastMessage, showToastMessageRequestError, showToastMessageUpdateRequest, showToastPromise } from "../../../../utils/toasts";
import { useGridFilter } from "../../../../contexts/GridFilterContext";
import { DownloadOutlined } from "@mui/icons-material";
import ShDataGrid2 from "../../../../components/theme/datagrid2/ShDataGrid2";
import MConfirmation from "../../../../components/global/Modals/MConfirmation";
import MLoader from "../../../../components/global/Modals/MLoader";
import SHQuickAction from "../../../../components/theme/Modal/SHQuickAction";
import CommentOutlinedIcon from '@mui/icons-material/CommentOutlined';

const SpOrderDetails = SidepanelLoader(() => import("../../../../components/global/Sidepanels/SpOrderDetails/SpOrderDetails"));

const GridActions = (props) => {
    const theme = useTheme();
    const {t} = useTranslation();
    return props.hasCreatePermission ?
        <ButtonGroup version={2}>
            <Shbutton 
                    className={"group-btn"} 
                    color={theme.palette.mode === "dark" ? "primaryGrey" : "grey"} 
                    onClick={() => props.exportProducts()}
                    endIcon={<DownloadOutlined/>}
                    variant="contained"
                    isLoading={props.isExporting}
                >
                    {t("export")}
                </Shbutton>
            <Shbutton 
                className={"group-btn"} 
                color={theme.palette.mode === "dark" ? "blue" : "primary"} 
                onClick={() => props.handleOpen(true)}
                endIcon={<AddIcon/>}
                variant="contained"
            >
                {t('new_order')}
            </Shbutton>
        </ButtonGroup> : <></>
}

let gridOptions = {
    export:true,
    disableCustomActionsOnPhone: true,
    isSidepanel: true,
    filter: [ORDER_FILTER_KEYS.PRODUCT, ORDER_FILTER_KEYS.B2B_ORDERS, ORDER_FILTER_KEYS.WEBSHOP],

};

const OutboundBackorderView = (props) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [isExtended, setIsExtended] = useState(false);
    const [spLoading, setSpLoading] = useState();
    
    const [config, setConfig] = useState({
        [ORDER_FILTER_KEYS.STATE.backend_key]:[general_states.BACKORDERED],
        "relationId": useSelector((state) => state.settings.environment.selectedId)
    });

    const hasCreatePermission = useSelector((state) => 
        selectUserPermission(state, [UserPrivileges.RP_ORDER_MANAGE])
    );

    // Multi-select state for bulk actions
    const [selectedItems, setSelectedItems] = useState([]);
    const [bulkUpdateOrders, { isLoading: updateLoading }] = useBulkUpdateOrdersMutation();
    const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);

    const onSelected = (selectedItems) => {
        setSelectedItems(selectedItems);
    };

    const [isUpdating, setIsUpdating] = useState(false);
    const [bulkCancelOrders, { isLoading: isBulkCancelling }] = useBulkCancelOrdersMutation();

    const handleCancelSelected = async () => {
        if (selectedItems.length === 0) return;

        if(isBulkCancelling) return;
        setIsUpdating(true);

        try {
            const response = await bulkCancelOrders({
                orderIds: selectedItems
            }).unwrap();

            setSelectedItems([]); // Clear selection after successful update
            showToastMessageUpdateRequest(t, response);
        } catch (error) {
            console.error('Failed to snooze orders:', error);
            showToastMessageRequestError(t("update_failed"), t, error);
        } finally {
            setIsUpdating(false);
        }
    };
    
    const [confirmSplitBackorderOpen, setConfirmSplitBackorderOpen] = useState(false);
    const [isSplitting, setIsSplitting] = useState(false);
    const [bulkSplitBackorders, { isLoading: isBulkSplitting }] = useBulkSplitBackordersMutation();

    // Splits the backordered quantities off the selected orders. Orders that are fully in
    // backorder are left untouched by the backend, those come back as skipped.
    const handleSplitBackorders = async () => {
        if (selectedItems.length === 0) return;

        if(isBulkSplitting) return;
        setIsSplitting(true);

        try {
            const response = await bulkSplitBackorders({
                orderIds: selectedItems
            }).unwrap();

            setSelectedItems([]);

            // The response counts how many orders were split, skipped and failed
            let toastType = "success";
            if (response.failedCount > 0) {
                toastType = "error";
            } else if (response.splitCount === 0) {
                toastType = "warning";
            }
            showToastMessage(toastType, response.message);
        } catch (error) {
            showToastMessageRequestError(t("update_failed"), t, error);
        } finally {
            setIsSplitting(false);
        }
    };

    const [addModalOpen, setAddModalOpen] = useState(false);
    const addModal = {
        open: addModalOpen,
        setOpen: setAddModalOpen
    }

    const handleOpen = () => {
        setAddModalOpen(true);
    }

    const [defaultTab, setDefaultTab] = useState(null);
    const handleClose = (response) => {
        setAddModalOpen(false);
        const newOrderState = response?.data?.state ?? response?.state;
        const newOrderId = response?.data?.id ?? response?.id;
        if (newOrderState === general_states.ACTION_REQUIRED_CUSTOMER && newOrderId) {
            if (!spLoading) setSpLoading(true);
            setOrderId(newOrderId);
            orderIdRef.current = newOrderId;
            setDefaultTab(2);
            setIsExtended(true);
        }
    };

    // const { index,id } = useGridFilter();

    // const filterObject = useSelector((state) => {
    //     const instance = state.gridFilter.viewInstances[index];
    //     return instance ? instance[id] : undefined;
    // });
    const [filter, setFilter] = useState({"state":["backordered"]});
    const [exportOrders, {isLoading: isExporting}] = useExportOrdersMutation();
    const exportOrdersCall = async () => {  
        const exportPromise = exportOrders(filter).unwrap();

        showToastPromise(
            exportPromise,
            t,
            {
                pending: t('export_processing') || 'Processing export...',
                success: t('export_success') || 'Export completed successfully',
                error: t('export_failed') || 'Export failed'
            }
        ).then((result) => {
            if (result.processing) {
                console.log('Export is being processed in background. Export ID:', result.exportId);
            }
        }).catch((error) => {
            console.error("Export failed", error);
        });
    }

    useEffect(() => {
        props.replaceView({
            ...props.config,
            gridOptions: gridOptions,
            mobileActions: <GridActions isExporting={isExporting}  
            hasCreatePermission={hasCreatePermission} 
            handleOpen={handleOpen} 
            exportProducts={exportOrdersCall}></GridActions>,
        });

        return () => {
            // console.log("unmounting warehouse");
        }
    },[config,isExporting,hasCreatePermission,exportOrdersCall,handleOpen]);

    const [orderId, setOrderId] = useState();
    const orderIdRef = useRef(orderId);
    
    const getOrderDetails = (params) => {
        if (!params) return;
    
        const neworderId = parseInt(params.row.id);
        // Only update state if the orderId has actually changed
        if (orderIdRef.current !== neworderId) {
            
            if(!spLoading) {
                setSpLoading(true);
            }
            setOrderId(neworderId);
            orderIdRef.current = neworderId; // Update the ref to the new value
        }
        setDefaultTab(null);
        setIsExtended(true);
    };

    const columns = [
        {
            field: "barcode",
            headerName: t('order'),
            flex:1,
            renderCell: (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }
                return (
                    <Box sx={{
                        minWidth:"100%",
                        height:"100%",
                        display:"flex",
                        alignItems:"center",
                        position:"relative",
                        whiteSpace:"nowrap",
                        wordBreak:"keep-all",
                        textOverflow:"ellipsis",
                        overflow:"hidden",
                        ".copyel": {
                            opacity:0,
                        },
                        "&:hover .copyel": {
                            opacity:1,
                        }
                    }}>
                        <Box
                            sx={{
                                minWidth:"100%",
                                height:"100%",
                                display:"flex",
                                alignItems:"center",
                                position:"relative",
                                whiteSpace:"nowrap",
                                wordBreak:"keep-all",
                                textOverflow:"ellipsis",
                                overflow:"hidden",
                                gap:2,

                            }}
                        >
                            <Text noBreak>{params.row.barcode}</Text>
                            {params.row.remark &&
                                <SHQuickAction
                                    disableScrollLock={true}
                                    component={
                                        <Box
                                            sx={{
                                                px:1.5,
                                                display:"flex",
                                                flexDirection:"column"
                                            }}
                                        >
                                            <Text light>{t("remark")}</Text>
                                            <Text>{params.row.remark}</Text>
                                        </Box>
                                    }
                                >
                                    {({anchorEl,onMouseEnter,onMouseLeave,onTouchStart}) => (
                                        <Box
                                            aria-owns={anchorEl ? 'simple-popover' : undefined}
                                            aria-haspopup={false}
                                            onMouseEnter={onMouseEnter}
                                            onMouseLeave={onMouseLeave}
                                            onTouchStart={onTouchStart}
                                            sx={{
                                                background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                                                height:32,
                                                width:32,
                                                color: colors.txt['secondary'],
                                                display:'flex',
                                                alignItems:'center',
                                                justifyContent:'center',
                                                borderRadius:4,
                                            }}
                                        >
                                            <CommentOutlinedIcon fontSize='small'/>
                                        </Box>
                                    )}
                                </SHQuickAction>
                            }
                        </Box>
                        <ShCopyButton 
                            styles={{button:{
                                background:theme.palette.mode === "dark" ? colors.grey[600] :  colors.grey[200],
                                color: colors.txt["primary"],
                                "&:hover" : {
                                    background:theme.palette.mode === "dark" ? colors.grey[500] :colors.grey[300],

                                }
                            }}} 
                            sx={{position:"absolute",right:0,zIndex:3000}} 
                            className={"copyel"} 
                            closeAfter={1500} 
                            value={params.row.barcode}
                        ></ShCopyButton>
                    </Box>
                );
            }
        },
        {field: "deliveryName", headerName: t("name"),flex:1, 
            renderCell: (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }
                return params.row.deliveryName
            }
        },
        {field: "webshopreference", headerName: t("webshopreference"),flex:2,
            renderCell: (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }
                return (<Text>{`${params.row.webshopcredentials?.description ?? t("self_created")} - ${params.row.webshopreference} (${params.row.deliveryCountry ? params.row.deliveryCountry.toUpperCase() : ""})`}</Text>)
            }
        },
        {field: "deliveryMethod", headerName: t("delivery_method"),flex:1, sortable:false,
            renderCell: (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }
               return t(params.row.deliveryMethod);
            }
        },
        {
            field: "date_planned", 
            headerName: t("date_planned_shipment"),
            flex:1,
            shFilter: {
                type: "date"
            },
            renderCell: (params) => {
                if(params.row.skeleton) {
                    return <ShLabelSkeleton/>
                }
                
                let planned = new Date(params.row.date_planned);
                let sent = params.row.date_sent ? new Date(params.row.date_sent) : null;
              
                return (
                    <ShLabel size={32} fitted variant="blue" palette="normal">
                        {!sent && planned.toLocaleDateString(i18n.language)}
                        {sent && sent.toLocaleDateString(i18n.language)}
                    </ShLabel>
                );
            }
        },
        {field: "orderlinesCount", headerName: t("orderlines_count"),flex:1, sortable: false,
        renderCell: (params) => {
            if(params.row.skeleton) {
                return <ShTextSkeleton/>
            }
            return params.row.amountOfLines
        }},
        {field: "relationRemark", headerName: t("internal_remark"),flex:1, sortable: false,
        renderCell: (params) => {
            if(params.row.skeleton) {
                return <ShTextSkeleton/>
            }
            return params.row.relationRemark
        }},
        {
            field: "state", 
            headerName: t("state"),
            flex:1,
            shFilter: {
                type: "date"
            },
            renderCell: (params) => {
                if(params.row.skeleton) {
                    return <ShLabelSkeleton/>
                }

                const { labelcolor, labeltext } = getOrderStateData(params.row.state, theme, false, params.row.snoozeWhenAvailable);
              
                return (
                    <ShLabel size={32} palette="normal" variant={labelcolor}>
                      {t(labeltext)}
                    </ShLabel>
                  );
              }
        },
        {
            field: "createdAt",
            headerName: t("date_created"),
            flex:1,
            grouping: RELATION_ORDERS_DEFAULT.CREATED_AT,
            renderCell: (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }

                const date = new Date(params.row.createdAt);
                const dateStr = date.toLocaleDateString(i18n.language, { dateStyle: 'short' });
                const timeStr = date.toLocaleTimeString(i18n.language, { timeStyle: 'short' });

                return (
                    <Text>
                        {dateStr}{' '}<Text light>{timeStr}</Text>
                    </Text>
                );
                }
        },
        {
            field: "phoneView", 
            headerName: "PhoneView",
            cellClassName: "phone-column--cell",
            renderCell: (params) => {
                return (
                    <OrdersCard onClick={() => getOrderDetails(params)} skeleton={params.row.skeleton ?? false} {...params.row}  />
                )
            }
        }
    ];

    return (
        <Box height={1} display={"flex"} flexDirection={"column"}>
            {/* <GridActions></GridActions> */}
            <ShDataGrid3 key={theme.palette.mode}
                gridKey={RELATION_ORDERS_FAMILY.BACKORDERED.gridKey}
                family={RELATION_ORDERS_FAMILY}
                content={useGetOrdersQuery}
                config={config}
                gridActions={<GridActions  isExporting={isExporting} hasCreatePermission={hasCreatePermission} handleOpen={handleOpen} exportProducts={exportOrdersCall}></GridActions>} 
                title={t('backorder') }
                gridOptions={gridOptions}
                enableGrouping={true}
                filterObjectChanged={(filterObject) => {
                    setFilter(filterObject?.filter);
                }}
                sortModel={{field: columns[8].field,type:"DESC"}}
                columns={columns}
                // rows={data}
                onRowClick={getOrderDetails}
                selectable
                setSelected={onSelected}
                selectedItems={selectedItems}
                selectedActions={
                    <ShDataGridSelectedActions selectedItems={selectedItems}>
                        <DropdownButton
                            onClick={() => setConfirmCancelOpen(true)}
                            icon={<CancelIcon/>}
                            text={t("cancel_selected")}
                            disabled={updateLoading}
                        />
                        {/* {hasCreatePermission && (
                            <DropdownButton
                                onClick={() => setConfirmSplitBackorderOpen(true)}
                                icon={<SplitscreenIcon/>}
                                text={t("split_backorder_lines")}
                                disabled={isBulkSplitting}
                            />
                        )} */}
                    </ShDataGridSelectedActions>
                }
            >
            </ShDataGrid3>

            {spLoading &&
                <SpOrderDetails
                    defaultTab={defaultTab}
                    isExtended={isExtended}
                    setIsExtended={setIsExtended}
                    orderId={orderId}
                    // data={orderData}
                    // relation={true}
                    canReserveOrderLines={true}
                />
            }
            <MpAddOrderRelation onOpen={() => addModal.setOpen(true)} onClose={() => addModal.setOpen(false)} open={addModal.open} handleClose={handleClose} />

            <MLoader
                open={isUpdating}
                message={t('cancelling_orders')}
            />

            <MLoader
                open={isSplitting}
                message={t('splitting_backorder_lines')}
            />

            <MConfirmation
                open={confirmCancelOpen}
                handleClose={() => setConfirmCancelOpen(false)}
                confirm={handleCancelSelected}
                title={t("cancel_selected")}
                subtitle={t("cancel_selected_confirm")}
            />

            <MConfirmation
                open={confirmSplitBackorderOpen}
                handleClose={() => setConfirmSplitBackorderOpen(false)}
                confirm={handleSplitBackorders}
                title={t("split_backorder_lines")}
                subtitle={t("mc_bulk_split_backorder_subtitle")}
                content={t("mc_bulk_split_backorder_content")}
            />
        </Box>
    );
}
export default OutboundBackorderView;